/**
 * 檔案用途區塊
 * @module payment-handlers
 * @purpose 集中管理 WalletConnect 支付流程中的錯誤建立、地址與網路判斷，以及 Bitcoin 多協議轉帳嘗試邏輯，供上層支付流程呼叫。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */

import { modal as appKitModal } from '@reown/appkit/react';
import { isAddress } from 'viem';
import { validate as validateBitcoinAddress, Network as BitcoinNetwork } from 'bitcoin-address-validation';
import { parseNamespace } from '@modules/walletconnect/config/token-network-adapter-mapping';

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義支付流程所需的參數結構，供頁面層與處理函式共用。
 * @purpose 約束付款地址、鏈別、金額與 token 相關欄位型別，避免跨層傳值不一致。
 */
export interface PaymentParams {
  /** 收款地址（對應 URL 參數 address） */
  walletAddress: string;
  /** CAIP-2 格式的 Chain ID（由 tokens + networkName 查 mapping 取得，如 eip155:1、bip122:...） */
  chainId: string;
  /** 支付金額字串（對應 URL 參數 amount，避免精度問題） */
  amount: string;
  /** 支付完成後跳轉的 URL（對應 URL 參數 redirectUrl） */
  redirectUrl: string;
  /** 幣種符號，用於 UI 顯示（由 mapping 或 URL 取得） */
  token?: string;
  /** 是否為 ERC20/BEP20 token（對應 URL 參數 isToken） */
  isToken?: boolean;
  /** ERC20 token 合約地址（對應 URL 參數 tokenAddress，ERC20 時必填） */
  contractAddress?: string;
  /** Token 精度（由 TOKEN_NETWORK_ADAPTER_MAPPING 提供） */
  decimals?: number;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義支付錯誤代碼聯集，供 UI 層以 i18n key 映射訊息。
 * @purpose 將可預期錯誤收斂為固定代碼，降低錯誤處理分支歧異。
 */
export type PaymentErrorCode =
  | 'networkMismatch'
  | 'invalidWalletAddress'
  | 'invalidContractAddress'
  | 'tokenAddressRequired'
  | 'bitcoinPaymentFailed'
  | 'generic';

/**
 * 宣告內容用途說明與單號關聯
 * @description 擴充 Error 型別以攜帶支付錯誤代碼。
 * @purpose 讓錯誤可同時保有例外堆疊與可翻譯代碼欄位。
 */
export interface PaymentError extends Error {
  code: PaymentErrorCode;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依指定錯誤代碼建立 PaymentError 物件。
 * @purpose 統一支付流程拋錯格式，供上層一致處理與顯示。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function createPaymentError(code: PaymentErrorCode, message?: string): PaymentError {
  const err = new Error(message ?? code) as PaymentError;
  err.code = code;
  return err;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 判斷未知錯誤是否符合 PaymentError 結構。
 * @purpose 在捕捉例外時安全縮小型別，便於讀取 code 欄位。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function isPaymentError(err: unknown): err is PaymentError {
  return err instanceof Error && 'code' in err && typeof (err as PaymentError).code === 'string';
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 定義地址驗證所支援的鏈類型。
 * @purpose 讓地址驗證流程以離散值切換 EVM 與 Bitcoin 檢查策略。
 */
export type AddressChainType = 'evm' | 'bitcoin';

/**
 * 宣告內容用途說明與單號關聯
 * @description 依鏈類型與網路別驗證地址格式合法性。
 * @purpose 將 EVM 與 Bitcoin 驗證細節封裝成單一入口，供上層重用。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
function validateAddress(address: string, chainType: AddressChainType, isTestnet: boolean = false): boolean {
  if (!address?.trim()) {
    return false;
  }
  if (chainType === 'evm') {
    return isAddress(address);
  }
  if (chainType === 'bitcoin') {
    const network = isTestnet ? BitcoinNetwork.testnet : BitcoinNetwork.mainnet;
    return validateBitcoinAddress(address, network);
  }
  return false;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 CAIP-2 namespace 選擇對應地址驗證規則。
 * @purpose 確保不同鏈別在付款前使用正確格式檢查，降低送單失敗率。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function isValidWalletAddressForChain(address: string, chainId: string): boolean {
  const namespace = parseNamespace(chainId);
  if (namespace === 'eip155') {
    return validateAddress(address, 'evm');
  }
  if (namespace === 'bip122') {
    return validateAddress(address, 'bitcoin', false);
  }
  return Boolean(address?.trim());
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 判斷錯誤訊息是否屬於使用者拒絕簽署或交易。
 * @purpose 區分可預期的人為取消情境與系統異常，改善錯誤提示策略。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function isUserRejectedError(error: Error): boolean {
  const errorMessage = error.message.toLowerCase();
  return (
    errorMessage.includes('user denied') ||
    errorMessage.includes('user rejected') ||
    errorMessage.includes('denied transaction') ||
    errorMessage.includes('rejected') ||
    errorMessage.includes('user cancelled') ||
    errorMessage.includes('user canceled') ||
    (errorMessage.includes('signature') && errorMessage.includes('denied'))
  );
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 chainId namespace 分類支付處理器類型。
 * @purpose 讓上層流程可先決定採用 EVM、Bitcoin 或不支援分支。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function getPaymentHandlerType(chainId: string): 'evm' | 'bitcoin' | 'unsupported' {
  const namespace = parseNamespace(chainId);
  if (namespace === 'eip155') {
    return 'evm';
  }
  if (namespace === 'bip122') {
    return 'bitcoin';
  }
  return 'unsupported';
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 驗證目前錢包網路是否與目標 chainId 一致。
 * @purpose 在送交易前攔截錯誤網路，避免發送至非預期鏈。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function checkNetwork(targetChainId: string, currentChainId: number | undefined): boolean {
  const namespace = parseNamespace(targetChainId);

  // EVM 鏈檢查
  if (namespace === 'eip155') {
    if (!currentChainId) {
      // 如果當前 chainId 未定義，可能是錢包還沒連接或還沒讀取到
      // 返回 false，讓上層處理
      return false;
    }

    const numericTargetChainId = Number(targetChainId.split(':')[1]);
    return currentChainId === numericTargetChainId;
  }

  // Bitcoin 鏈檢查
  // TODO: 實作 Bitcoin 網路檢查邏輯
  // Bitcoin 的 chainId 格式是 bip122:000000000019d6689c085ae165831e93
  // 需要從 BitcoinAdapter 或錢包取得當前 Bitcoin 網路資訊
  if (namespace === 'bip122') {
    // 暫時返回 true，因為 Bitcoin 通常只有主網和測試網
    // 未來需要實作完整的 Bitcoin 網路檢查
    return true;
  }

  // 其他鏈（如 TRON、Solana 等）
  // TODO: 實作其他鏈的網路檢查邏輯
  return true;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 從不同 RPC 回傳型別中抽取交易 txid。
 * @purpose 統一處理字串或物件格式結果，簡化後續發送流程判斷。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
function extractTxId(result: unknown): string | null {
  if (typeof result === 'string' && result.length > 0) {
    return result;
  }

  if (
    result &&
    typeof result === 'object' &&
    'txid' in result &&
    typeof (result as { txid: string }).txid === 'string'
  ) {
    return (result as { txid: string }).txid;
  }

  return null;
}

type BitcoinProvider = { request: (params: unknown, chainId?: string) => Promise<unknown> };

type BitcoinConnectorSendTransfer = (params: { recipient: string; amount: string }) => Promise<string>;

/**
 * 宣告內容用途說明與單號關聯
 * @description 嘗試透過 connector 的 sendTransfer API 發送 Bitcoin 交易。
 * @purpose 在可用時優先走 connector 原生能力，提升成功率與相容性。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function trySendTransferViaConnector(
  walletProvider: unknown,
  walletAddress: string,
  amount: string,
): Promise<string | null> {
  const connector = walletProvider as { sendTransfer?: BitcoinConnectorSendTransfer };
  if (typeof connector.sendTransfer !== 'function') {
    return null;
  }
  try {
    const txid = await connector.sendTransfer({ recipient: walletAddress, amount });
    return txid && typeof txid === 'string' ? txid : null;
  } catch {
    return null;
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 透過 WalletConnect 的 getAccountAddresses 與 sendTransfer 組合送出交易。
 * @purpose 適配 WC 協議型錢包的 Bitcoin 轉帳流程。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function trySendTransferWalletConnect(
  walletProvider: BitcoinProvider,
  walletAddress: string,
  amount: string,
  chainId: string,
): Promise<string | null> {
  try {
    const addressesRes: unknown = await walletProvider.request(
      { method: 'getAccountAddresses', params: undefined },
      chainId,
    );
    const addresses = Array.isArray(addressesRes) ? addressesRes : [];
    const first: unknown = addresses[0];
    const account =
      typeof first === 'string'
        ? first
        : first && typeof first === 'object' && 'address' in first
          ? String((first as { address: string }).address)
          : undefined;

    if (!account) {
      return null;
    }

    const result = await walletProvider.request(
      {
        method: 'sendTransfer',
        params: { account, recipientAddress: walletAddress, amount },
      },
      chainId,
    );
    return extractTxId(result);
  } catch {
    return null;
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依 Sats Connect 參數格式呼叫 sendTransfer 發送交易。
 * @purpose 支援以 recipients 陣列與數值金額的錢包協議差異。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function trySendTransferSatsConnect(
  walletProvider: BitcoinProvider,
  walletAddress: string,
  amount: string,
): Promise<string | null> {
  try {
    const parsedAmount = Number(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      return null;
    }

    const result = await walletProvider.request({
      method: 'sendTransfer',
      params: { recipients: [{ address: walletAddress, amount: parsedAmount }] },
    });
    return extractTxId(result);
  } catch {
    return null;
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 嘗試以 bitcoin_sendTransfer RPC 方法發送交易。
 * @purpose 覆蓋舊版或特定錢包命名差異的 Bitcoin 轉帳入口。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function tryBitcoinSendTransfer(
  walletProvider: BitcoinProvider,
  walletAddress: string,
  amount: string,
): Promise<string | null> {
  try {
    const result = await walletProvider.request({
      method: 'bitcoin_sendTransfer',
      params: { to: walletAddress, amount },
    });
    return extractTxId(result);
  } catch {
    return null;
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 嘗試以 btc_sendTransfer RPC 方法發送交易。
 * @purpose 補齊不同錢包對 Bitcoin 轉帳方法命名的相容層。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function tryBtcSendTransfer(
  walletProvider: BitcoinProvider,
  walletAddress: string,
  amount: string,
): Promise<string | null> {
  try {
    const result = await walletProvider.request({
      method: 'btc_sendTransfer',
      params: { to: walletAddress, amount },
    });
    return extractTxId(result);
  } catch {
    return null;
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 嘗試以通用 sendTransaction 方法與 Bitcoin chainId 發送交易。
 * @purpose 作為多協議皆失敗時的最後備援路徑。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function trySendTransaction(
  walletProvider: BitcoinProvider,
  walletAddress: string,
  amount: string,
): Promise<string | null> {
  try {
    const result = await walletProvider.request({
      method: 'sendTransaction',
      params: {
        to: walletAddress,
        value: amount,
        chainId: 'bip122:000000000019d6689c085ae165831e93',
      },
    });
    return extractTxId(result);
  } catch {
    return null;
  }
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依序嘗試多種 Bitcoin 發送方法並回傳第一個成功 txid。
 * @purpose 透過級聯備援提高不同錢包與協議下的交易送出成功率。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
async function tryAllBitcoinSendMethods(
  provider: BitcoinProvider,
  walletAddress: string,
  amount: string,
  chainId: string,
): Promise<string | null> {
  return (
    (await trySendTransferWalletConnect(provider, walletAddress, amount, chainId)) ||
    (await trySendTransferSatsConnect(provider, walletAddress, amount)) ||
    (await tryBitcoinSendTransfer(provider, walletAddress, amount)) ||
    (await tryBtcSendTransfer(provider, walletAddress, amount)) ||
    (await trySendTransaction(provider, walletAddress, amount))
  );
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 執行 Bitcoin 支付主流程，先取 provider 再走 connector 或 RPC 發送。
 * @purpose 整合 Bitcoin 付款入口並在失敗時回傳標準化錯誤代碼。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export async function handleBitcoinPayment(paymentParams: PaymentParams): Promise<string> {
  const appKitInstance = appKitModal;
  if (!appKitInstance) {
    throw createPaymentError('generic');
  }

  const walletProvider = await appKitInstance.getWalletProvider();
  if (!walletProvider || typeof walletProvider !== 'object') {
    throw createPaymentError('generic');
  }

  if (!('request' in walletProvider) || typeof walletProvider.request !== 'function') {
    throw createPaymentError('generic');
  }

  const { walletAddress, amount, chainId } = paymentParams;
  const typedProvider = walletProvider as BitcoinProvider;

  // 優先使用 connector 的 sendTransfer()（@reown/appkit-adapter-bitcoin API）
  const txHash =
    (await trySendTransferViaConnector(walletProvider, walletAddress, amount)) ??
    (await tryAllBitcoinSendMethods(typedProvider, walletAddress, amount, chainId));

  if (!txHash) {
    throw createPaymentError('bitcoinPaymentFailed');
  }

  return txHash;
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:18:05.564Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note 已僅針對註解進行重構：補上頂部「檔案用途區塊」、為主要宣告新增/統一「宣告內容用途說明與單號關聯」JSDoc（含指定標籤），並追加底部「llm 分析紀錄區」。@external 全數使用 IN-111889 的 Jira 完整網址，未改動任何程式邏輯。
 */
